import OutlineButton from "@components/Buttons/OutlineButton";
import { PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea } from "@components/Forms";
import { ChevronUpIcon } from "@components/Icons";
import PreviewSection from "@components/Preview/PreviewSection";
import DocumentTable from "@components/Tables/DocumentTable";
import TabsContainer from "@components/Tabs/TabsContainer";
import { Breadcrumb, SelectComponentModal } from "@components/UI";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import CustomSelect from "@components/UI/CustomSelect";
import LicenseSelectionModal from "@components/UI/LicenseSelectionModal";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant, { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useFetchData } from "@hooks/useFetchData";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import { attachLicenseAttributes } from "@utils/licenseAttribute";
import { STORES, updateDataInIndexedDb } from "@utils/indexedDb";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CHARACTER_LENGTH } from "@constants/index";

const TEST_ID = "preview-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const PreviewDetail = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { projectPreviewSeq } = useParams();
  const navigate = useNavigate();
  const isFirstRender = useRef(true);
  const [modalSelectLicenseOpen, setModalSelectLicenseOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [modalSelectComponentOpen, setModalSelectComponentOpen] = useState(false);
  const { creatorList, fetchSearchInfo } = useSearchInfo();
  const [fileGroupNo, setFileGroupNo] = useState(null);
  const previousFileGroupNoZero = useRef(null);
  const { uploadFilesAndGetOnSuccess } = useFileGroupAPI(fileGroupNo || 0);

  const [previewData, setPreviewData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [fileInfoModified, setFileInfoModified] = useState([]);
  const [documentTotalData, setDocumentTotalData] = useState(0);
  const [isPreviewInfoCollapsed, setIsPreviewInfoCollapsed] = useState(false);
  const [previewSectionRefreshTrigger, setPreviewSectionRefreshTrigger] = useState(0);
  const [resetSelectionTrigger, setResetSelectionTrigger] = useState(0);
  const [licensePolicyList, setLicensePolicyList] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [fileUploadTrigger, setFileUploadTrigger] = useState(0);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [documentCurrentPage, setDocumentCurrentPage] = useState(1);
  const [fileDeleteTrigger, setFileDeleteTrigger] = useState(0);
  const [activePreviewInfoTab, setActivePreviewInfoTab] = useState(0);

  // Mutation for getting license attribute
  const getLicenseAttribute = useMutationData(
    () => queryKeys.licenseAttributeByLicenseIdentifierBatch,
    { method: "POST" },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error during get data:", error);
        showErrorToast(t("common.get-license-attribute-error"));
      },
    },
  );

  const mapToPreviewComponent = (component) => ({
    id: component.componentSeq,
    projectPreviewComponentSeq: -1,
    componentName: component.componentName,
    componentVersion: component.componentVersion,
    licenseId: component.licenseId,
    licenseIdentifier: component.licenseIdentifier,
    licenseName: component.licenseName,
    licenseTypeCd: component.licenseTypeCd,
    cpe: component.cpe,
    homepageUrl: component.homepageUrl,
    downloadUrl: component.downloadUrl,
    copyright: component.copyright,
    description: component.description,
    remark: component.remark,
    weaknessCd: component.weaknessCd,
    db: false,
    delete: false,
  });

  // Function to handle component selection from modal
  const handleComponentSelection = async (selectionData) => {
    if (!previewData) return;

    try {
      const { newComponents, deselectedComponents } = selectionData;

      const componentsToTransform = newComponents.map(mapToPreviewComponent);
      const transformedNewComponents = await attachLicenseAttributes(
        componentsToTransform,
        (payload) => getLicenseAttribute.mutateAsync(payload),
      );

      // Get current preview components
      const currentComponents = previewData.projectPreviewComponents || [];

      // Mark deselected components as deleted
      const updatedCurrentComponents = currentComponents.map((component) => {
        const isDeselected = deselectedComponents.some(
          (deselectedComp) =>
            component.componentName === deselectedComp.componentName &&
            component.componentVersion === deselectedComp.componentVersion &&
            component.licenseId === deselectedComp.licenseId &&
            component.licenseIdentifier === deselectedComp.licenseIdentifier &&
            component.licenseName === deselectedComp.licenseName &&
            component.licenseTypeCd === deselectedComp.licenseTypeCd &&
            component.cpe === deselectedComp.cpe &&
            component.homepageUrl === deselectedComp.homepageUrl &&
            component.downloadUrl === deselectedComp.downloadUrl &&
            component.copyright === deselectedComp.copyright &&
            component.description === deselectedComp.description &&
            component.remark === deselectedComp.remark &&
            component.weaknessCd === deselectedComp.weaknessCd,
        );

        return {
          ...component,
          delete: isDeselected ? true : component.delete || false,
        };
      });

      // Add new components to existing ones
      const updatedComponents = [...updatedCurrentComponents, ...transformedNewComponents];

      // Prepare payload for update
      const payload = {
        projectPreviewSeq: previewData.projectPreviewSeq,
        projectPreviewName: previewData.projectPreviewName,
        projectPreviewStatusCd: previewData.projectPreviewStatusCd || "",
        projectPreviewComment: previewData.projectPreviewComment || "",
        companyCd: previewData.companyCd,
        licensePolicySeq: previewData.licensePolicySeq,
        licensePolicyName: previewData.licensePolicyName,
        fileInfoSeqListToDelete: [],
        projectPreviewComponents: updatedComponents,
      };
      // Call update API
      updateProjectPreview.mutate(payload);
      // Close modal
      setModalSelectComponentOpen(false);
    } catch (error) {
      console.error("Error adding components:", error);
    }
  };

  // Create a custom mutation for getting file information with dynamic URL and pagination (fileGroupNo as query param)
  const getFileInfoMutation = useMutationData(
    ({ fileGroupNo, page = 1, pageSize = 5 }) => {
      const startRow = (page - 1) * pageSize;
      return `${queryKeys.getFileInfo}?fileGroupNo=${fileGroupNo}&pageSize=${pageSize}&startRow=${startRow}`;
    },
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response && Array.isArray(response.object)) {
          setFileInfoModified(response.object);
          setDocumentTotalData(response.objectCount || response.object.length);
        } else if (Array.isArray(response)) {
          setFileInfoModified(response);
          setDocumentTotalData(response.length);
        } else {
          setFileInfoModified([]);
          setDocumentTotalData(0);
        }
      },
      onError: (error) => {
        console.error("Error during get file info:", error);
        showErrorToast(t("common.get-file-info-error"));
        setFileInfoModified([]);
        setDocumentTotalData(0);
      },
    },
  );

  // API calls for dropdown data - using the same endpoint as AddPreview
  const { data: licensePolicyData, error: licensePolicyError } = useFetchData(
    [queryKeys.licensePoliciesOnly],
    queryKeys.licensePoliciesOnly,
    { method: "GET" },
    {},
    {},
  );

  // API to update project preview
  const updateProjectPreview = useMutationData(
    queryKeys.updateProjectPreview,
    {
      method: "PUT",
    },
    {
      onSuccess: async (response, variables) => {
        if (typeof response !== "string") {
          await updateDataInIndexedDb(STORES.PREVIEWS, response, response.projectPreviewSeq);
          // Check if this is a file deletion operation
          const isFileDeletion = variables?.fileInfoSeqListToDelete?.length > 0;

          if (isFileDeletion) {
            // Show success message for file deletion
            showSuccessToast(t("common.file-delete-success"));

            // Immediately remove deleted files from the UI
            const deletedFileSeqs = variables.fileInfoSeqListToDelete;
            setFileInfoModified((prevFiles) => prevFiles.filter((file) => !deletedFileSeqs.includes(file.fileInfoSeq)));

            // Update document count
            const newTotal = Math.max(0, documentTotalData - deletedFileSeqs.length);
            setDocumentTotalData(newTotal);

            // Handle pagination: if current page becomes empty and it's not the first page, go to previous page
            const itemsPerPage = 5;
            const currentPageItems = fileInfoModified.length;
            const deletedItems = deletedFileSeqs.length;
            const remainingItems = currentPageItems - deletedItems;

            if (remainingItems === 0 && documentCurrentPage > 1) {
              setDocumentCurrentPage((prev) => prev - 1);
            }

            // Trigger file info refresh after a short delay to ensure backend processing is complete
            setTimeout(() => {
              setFileDeleteTrigger((prev) => prev + 1);
            }, 500);
          }

          // Refresh data after successful update
          getData.refetch();
          // Trigger PreviewSection refresh
          setPreviewSectionRefreshTrigger((prev) => prev + 1);
          // Reset selection after successful deletion
          setResetSelectionTrigger((prev) => prev + 1);
        }
      },
      onError: (error) => {
        console.error("Error during update project preview:", error);
        // Check if this is a file deletion error by looking at the payload
        const isFileDeletionError =
          error?.config?.data && JSON.parse(error.config.data).fileInfoSeqListToDelete?.length > 0;

        if (isFileDeletionError) {
          showErrorToast(t("common.file-delete-error"));
        } else {
          showErrorToast(t("project-preview.update-project-preview-error"));
        }
      },
    },
  );

  // Custom upload mutation that also refreshes document count
  const uploadFilesWithRefresh = useMutationData(
    queryKeys.uploadFiles,
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onMutate: () => {
        setIsFileUploading(true);
      },
      onSuccess: () => {
        showSuccessToast(t("common.files-upload-success"));
        setIsFileUploading(false);
        // Refresh preview data first to get updated fileGroupNo if needed
        getData.refetch();
        // Trigger file upload refresh after a short delay to ensure backend processing is complete
        setTimeout(() => {
          setFileUploadTrigger((prev) => prev + 1);
        }, 1000);
      },
      onError: (error) => {
        console.error("Error during upload data:", error);
        setIsFileUploading(false);
        showErrorToast(t("common.files-upload-error"));
      },
    },
  );

  const getLicenseDefault = useMutationData(
    queryKeys.getListLicenseAttributes,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const defaultLicense = response.find((item) => item.defaultFlag === "Y");
          if (defaultLicense) {
            setSelectedLicense(defaultLicense);
          }
        }
      },
      onError: (error) => {
        console.error("Error during get license data:", error);
        showErrorToast(t("common.get-license-attribute-error"));
      },
    },
  );

  // API call for preview details
  const queryKey = [queryKeys.getPreviewDetail, projectPreviewSeq];
  const queryUrl = `/project_preview/${projectPreviewSeq}`;

  const getData = useFetchData(
    queryKey,
    queryUrl,
    {
      method: "GET",
    },
    {},
    {
      enabled: !!projectPreviewSeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (getData?.error?.response?.status === 404) {
      navigate("/404", { state: { backTo: "/project-previews" } });
    }
  }, [getData?.error, navigate]);

  // API call for updating preview
  const updatePreview = useMutationData(
    "project_preview",
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        // Set updating state to true
        setIsUpdating(true);
        getData.refetch();
        showSuccessToast(t("project.update-project-preview-successfully"));
      },
      onError: (error) => {
        console.error("Error during update preview:", error);
        setIsUpdating(false);
        // Check for specific error messages
        const errorMsg = error.response?.status === 400 && error.response?.data ? error.response.data : "";
        let subErrMsg = "";
        switch (errorMsg) {
          case "Project preview name already exists":
            subErrMsg = t("project.project-preview.preview-name-already-existed");
            break;
          default:
            subErrMsg = "";
        }
        ToastConstant(TOAST.error, t("project.update-project-preview-error"), subErrMsg);
      },
    },
  );

  // Handle license policy data from API
  useEffect(() => {
    if (licensePolicyData) {
      const licensePolicyOptions = licensePolicyData.map(({ licensePolicySeq, policyName }) => ({
        value: licensePolicySeq,
        label: policyName,
      }));
      setLicensePolicyList(licensePolicyOptions);
    }
  }, [licensePolicyData, licensePolicyError]);

  // Refresh PreviewSection when license policy changes in edit mode
  useEffect(() => {
    if (isEditMode && editFormData.licensePolicySeq) {
      setPreviewSectionRefreshTrigger((prev) => prev + 1);
    }
  }, [isEditMode, editFormData.licensePolicySeq]);

  // Trigger API calls when component mounts
  useEffect(() => {
    if (isFirstRender.current) {
      getLicenseDefault.mutate();
      fetchSearchInfo();
      isFirstRender.current = false;
    }
  }, []);

  // Process preview data when it's available
  useEffect(() => {
    if (getData.data && !getData.isLoading && !getData.error) {
      const response = getData.data;
      if (typeof response !== "string") {
        setPreviewData(response);
        // Update fileGroupNo if available
        if (response.fileGroupNo !== undefined && response.fileGroupNo !== null) {
          setFileGroupNo(response.fileGroupNo);
          if (response.fileGroupNo === 0) {
            previousFileGroupNoZero.current = response.fileGroupNo;
          }
        }

        // Reset edit mode after data is refreshed
        if (isUpdating) {
          setIsEditMode(false);
          setEditFormData({});
          setIsUpdating(false);
        }
      }
    }
  }, [getData.data, getData.isLoading, getData.error, isUpdating]);

  const displayPreviewData = getData.data || previewData;

  // Handle edit mode functions
  const handleEditClick = () => {
    if (displayPreviewData) {
      setEditFormData({
        projectPreviewName: displayPreviewData.projectPreviewName || "",
        createrName: displayPreviewData.createrName || "",
        createdAt: displayPreviewData.createdAt || "",
        updaterName: displayPreviewData.updaterName || "",
        updatedAt: displayPreviewData.updatedAt || "",
        licensePolicySeq: displayPreviewData.licensePolicySeq || "",
        projectPreviewComment: displayPreviewData.projectPreviewComment || "",
      });
      setIsEditMode(true);
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({});
    setIsUpdating(false);
  };

  const handleSaveEdit = () => {
    // Prepare the data for the API call - include all current data with edited fields
    const updateData = {
      // Include all current preview data
      ...displayPreviewData,
      // Override with edited fields
      projectPreviewName: editFormData.projectPreviewName,
      projectPreviewComment: editFormData.projectPreviewComment,
      licensePolicySeq: editFormData.licensePolicySeq,
    };

    // Call the API to update the preview
    updatePreview.mutate(updateData);
  };

  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLicenseSelect = (selectedLicense) => {
    setSelectedLicense(selectedLicense);
    if (selectedLicense) {
      setEditFormData((prev) => ({
        ...prev,
        licenseName: selectedLicense.licenseName,
        licenseIdentifier: selectedLicense.licenseIdentifier,
      }));
    } else {
      setEditFormData((prev) => ({
        ...prev,
        licenseName: "",
        licenseIdentifier: "",
      }));
    }
  };

  // Document delete modal state
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  const handleDeleteDocument = (documentToDelete) => {
    setDocumentToDelete(documentToDelete);
    setShowDeleteDocumentModal(true);
  };

  // Document management functions
  const handleConfirmDeleteDocument = () => {
    if (!documentToDelete) return;
    setDeleteDocumentLoading(true);

    // Get current preview data
    const currentPreviewData = previewData || displayPreviewData;
    if (!currentPreviewData) {
      setDeleteDocumentLoading(false);
      return;
    }

    // Prepare payload for update with fileInfoSeq to delete
    const payload = {
      projectPreviewSeq: currentPreviewData.projectPreviewSeq,
      projectPreviewName: currentPreviewData.projectPreviewName,
      projectPreviewStatusCd: currentPreviewData.projectPreviewStatusCd || "",
      projectPreviewComment: currentPreviewData.projectPreviewComment || "",
      companyCd: currentPreviewData.companyCd,
      licensePolicySeq: currentPreviewData.licensePolicySeq,
      licensePolicyName: currentPreviewData.licensePolicyName,
      fileInfoSeqListToDelete: [documentToDelete.fileInfoSeq], // Add the fileInfoSeq to delete list
      projectPreviewComponents: currentPreviewData.projectPreviewComponents || [],
    };

    // Call update API to delete the file
    updateProjectPreview.mutate(payload, {
      onSettled: () => {
        setDeleteDocumentLoading(false);
        setShowDeleteDocumentModal(false);
        setDocumentToDelete(null);
      },
    });
  };

  const handleUploadFile = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = "*/*";
    fileInput.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (
        files.length > 0 &&
        previewData?.fileGroupNo !== undefined &&
        previewData?.fileGroupNo !== null &&
        projectPreviewSeq
      ) {
        const uploadFilesData = files.reduce((acc, file, index) => {
          if (file instanceof File) {
            acc[`file_${index}`] = file;
          }
          return acc;
        }, {});

        const formData = new FormData();
        formData.append("targetSeq", projectPreviewSeq);
        formData.append("targetTypeCd", "PROJECT_PREVIEW");
        formData.append("fileGroupNo", previewData.fileGroupNo);

        Object.entries(uploadFilesData).forEach(([key, file]) => {
          formData.append(key, file, file.name);
        });

        // Use uploadFilesWithRefresh and then trigger manual refresh
        uploadFilesWithRefresh.mutate(formData, {
          onSuccess: () => {},
        });
      }
    };

    fileInput.click();
  };

  const handleDeleteComponents = (selectedRows) => {
    if (!selectedRows || selectedRows.length === 0) {
      return;
    }

    // Get current preview data
    const currentPreviewData = previewData || displayPreviewData;
    if (!currentPreviewData) {
      return;
    }

    // Create updated components list with delete flag set to true for selected components
    const updatedComponents = currentPreviewData.projectPreviewComponents.map((component) => {
      const isSelected = selectedRows.includes(component.projectPreviewComponentSeq);
      return {
        ...component,
        delete: isSelected, // Set delete flag to true for selected components
      };
    });

    // Prepare payload for update
    const payload = {
      projectPreviewSeq: currentPreviewData.projectPreviewSeq,
      projectPreviewName: currentPreviewData.projectPreviewName,
      projectPreviewStatusCd: currentPreviewData.projectPreviewStatusCd || "",
      projectPreviewComment: currentPreviewData.projectPreviewComment || "",
      companyCd: currentPreviewData.companyCd,
      licensePolicySeq: currentPreviewData.licensePolicySeq,
      licensePolicyName: currentPreviewData.licensePolicyName,
      fileInfoSeqListToDelete: [],
      projectPreviewComponents: updatedComponents,
    };
    // Call update API
    updateProjectPreview.mutate(payload);
  };

  // Tab configurations
  const previewInfoTabs = [
    {
      label: t("project.project-preview.general-information"),
      content: (
        <div className="relative" data-testid={tid("general-info-tab")}>
          {/* Header with title and Edit/Save/Cancel buttons */}
          <div className="flex items-center justify-between pb-4" data-testid={tid("general-info-header")}>
            <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={tid("general-info-title")}>
              {t("project.project-preview.general-information")}
            </h3>
            {isEditMode ? (
              <div className="flex space-x-3" data-testid={tid("general-info-edit-actions")}>
                <OutlineButton data-testid={tid("cancel-button")} onClick={handleCancelEdit}>{t("common.cancel-button")}</OutlineButton>
                <PrimaryButton
                  data-testid={tid("save-button")}
                  onClick={handleSaveEdit}
                  disabled={
                    updatePreview.isPending ||
                    !editFormData.projectPreviewName?.trim() ||
                    !editFormData.licensePolicySeq
                  }
                >
                  {t("common.save-button")}
                </PrimaryButton>
              </div>
            ) : (
              <OutlineButton data-testid={tid("edit-button")} onClick={handleEditClick}>
                {t("project.imported-components.edit-information")}
              </OutlineButton>
            )}
          </div>

          {/* Content Grid */}
          <div className="space-y-2 px-4" data-testid={tid("general-info-grid")}>
            {/* Row 1 */}
            <div className={`grid grid-cols-5 gap-x-4 gap-y-3 border-t ${colors.borderSection} pt-3`} data-testid={tid("general-info-row-1")}>
              <div data-testid={fieldContainerTestId("preview-name")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("preview-name")}>
                  {t("project.preview-name")} {isEditMode && <span className={colors.textRequired}>*</span>}
                </label>
                {isEditMode ? (
                  <CustomInput
                    type="text"
                    value={editFormData.projectPreviewName || ""}
                    onChange={(e) => handleInputChange("projectPreviewName", e.target.value)}
                    size="sm"
                    maxLength={256}
                    data-testid={fieldInputTestId("preview-name")}
                  />
                ) : (
                  <p
                    className={`text-sm overflow-hidden whitespace-nowrap text-ellipsis ${colors.textPrimary} h-8 leading-8 ${getData.isLoading || !displayPreviewData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    title={getData.isLoading || !displayPreviewData ? "" : displayPreviewData.projectPreviewName}
                    data-testid={fieldValueTestId("preview-name")}
                  >
                    {getData.isLoading || !displayPreviewData ? "" : displayPreviewData.projectPreviewName}
                  </p>
                )}
              </div>
              <div data-testid={fieldContainerTestId("creator")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("creator")}>
                  {t("system.creator")}
                </label>
                {isEditMode ? (
                  <CustomInput
                    type="text"
                    value={formatUserNameWithId(displayPreviewData?.createrName, displayPreviewData?.createrId)}
                    onChange={(e) => handleInputChange("createrName", e.target.value)}
                    disabled
                    size="sm"
                    data-testid={fieldInputTestId("creator")}
                  />
                ) : (
                  <div
                    className={`min-h-8 flex items-center ${getData.isLoading || !displayPreviewData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    data-testid={fieldValueTestId("creator")}
                  >
                    {!(getData.isLoading || !displayPreviewData) && (
                      <UserNameWithId name={displayPreviewData.createrName} id={displayPreviewData.createrId} layout="inline" />
                    )}
                  </div>
                )}
              </div>
              <div data-testid={fieldContainerTestId("create-at")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("create-at")}>
                  {t("common.create-at")}
                </label>
                {isEditMode ? (
                  <CustomInput
                    type="text"
                    value={editFormData.createdAt ? formatDateTime(editFormData.createdAt) : ""}
                    onChange={(e) => handleInputChange("createdAt", e.target.value)}
                    disabled
                    size="sm"
                    data-testid={fieldInputTestId("create-at")}
                  />
                ) : (
                  <p
                    className={`text-sm ${colors.textPrimary} h-8 flex items-center ${getData.isLoading || !displayPreviewData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    data-testid={fieldValueTestId("create-at")}
                  >
                    {getData.isLoading || !displayPreviewData
                      ? ""
                      : displayPreviewData.createdAt
                        ? formatDateTime(displayPreviewData.createdAt)
                        : ""}
                  </p>
                )}
              </div>
              <div data-testid={fieldContainerTestId("last-edited")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("last-edited")}>
                  {t("common.last-modified-by")}
                </label>
                {isEditMode ? (
                  <CustomInput
                    type="text"
                    value={formatUserNameWithId(displayPreviewData?.updaterName, displayPreviewData?.updaterId)}
                    onChange={(e) => handleInputChange("updaterName", e.target.value)}
                    disabled
                    size="sm"
                    data-testid={fieldInputTestId("last-edited")}
                  />
                ) : (
                  <div
                    className={`min-h-8 flex items-center ${getData.isPending || !displayPreviewData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    data-testid={fieldValueTestId("last-edited")}
                  >
                    {!(getData.isPending || !displayPreviewData) && (
                      <UserNameWithId name={displayPreviewData.updaterName} id={displayPreviewData.updaterId} layout="inline" />
                    )}
                  </div>
                )}
              </div>
              <div data-testid={fieldContainerTestId("last-modified")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("last-modified")}>
                  {t("common.last-modified-date")}
                </label>
                {isEditMode ? (
                  <CustomInput
                    type="text"
                    value={editFormData.updatedAt ? formatDateTime(editFormData.updatedAt) : ""}
                    onChange={(e) => handleInputChange("updatedAt", e.target.value)}
                    disabled
                    size="sm"
                    data-testid={fieldInputTestId("last-modified")}
                  />
                ) : (
                  <p
                    className={`text-sm ${colors.textPrimary} h-8 flex items-center ${getData.isPending || !displayPreviewData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    data-testid={fieldValueTestId("last-modified")}
                  >
                    {getData.isPending || !displayPreviewData
                      ? ""
                      : displayPreviewData.updatedAt
                        ? formatDateTime(displayPreviewData.updatedAt)
                        : ""}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2 */}
            <div className={`grid truncate grid-cols-5 gap-x-4 gap-y-3 pt-3 border-t ${colors.borderSection}`} data-testid={tid("general-info-row-2")}>
              <div data-testid={fieldContainerTestId("license-policy")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("license-policy")}>
                  {t("project.clarity.license")} {isEditMode && <span className={colors.textRequired}>*</span>}
                </label>
                {isEditMode ? (
                  <CustomSelect
                    placeholder={!licensePolicyData ? "Loading..." : t("common.please-select")}
                    value={licensePolicyList.find((opt) => opt.value === editFormData.licensePolicySeq) || null}
                    onChange={(option) => {
                      handleInputChange("licensePolicySeq", option?.value || null);
                    }}
                    options={licensePolicyList}
                    size="sm"
                    className="w-full"
                    clearable={false}
                    menuPlacement="bottom"
                    menuPosition="fixed"
                    data-testid={fieldInputTestId("license-policy")}
                  />
                ) : (
                  <p
                    className={`text-sm overflow-hidden whitespace-nowrap text-ellipsis ${colors.textPrimary} h-8 leading-8 ${getData.isLoading || !displayPreviewData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    title={getData.isPending || !displayPreviewData ? "" : displayPreviewData.licensePolicyName || ""}
                    data-testid={fieldValueTestId("license-policy")}
                  >
                    {getData.isPending || !displayPreviewData ? "" : displayPreviewData.licensePolicyName || ""}
                  </p>
                )}
              </div>
              <div className="col-span-4" data-testid={fieldContainerTestId("preview-comment")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("preview-comment")}>
                  {t("project.preview-comment")}
                </label>
                {isEditMode ? (
                  <>
                    <CustomTextarea
                      rows={2}
                      value={editFormData.projectPreviewComment || ""}
                      onChange={(e) => handleInputChange("projectPreviewComment", e.target.value)}
                      placeholder={t("project.project-preview.write-comment-placeholder")}
                      maxLength={CHARACTER_LENGTH.COMMENT}
                      data-testid={fieldInputTestId("preview-comment")}
                    />
                    <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("preview-comment-char-count")}>
                      {(editFormData.projectPreviewComment || "").length}/{CHARACTER_LENGTH.COMMENT}
                    </p>
                  </>
                ) : (
                  <p
                    className={`text-sm overflow-hidden whitespace-nowrap text-ellipsis ${colors.textPrimary} h-8 leading-8 ${getData.isLoading || !displayPreviewData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    title={displayPreviewData?.projectPreviewComment}
                    data-testid={fieldValueTestId("preview-comment")}
                  >
                    {getData.isLoading || !displayPreviewData ? "" : displayPreviewData.projectPreviewComment || ""}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      label: `${t("common.documents")} (${documentTotalData || 0})`,
      content: (() => {
        const [documentItemsPerPage] = useState(5);
        const documentTotalPages = Math.ceil((documentTotalData || 0) / documentItemsPerPage);

        // Fetch paginated data when page or page size changes
        useEffect(() => {
          if (fileGroupNo !== undefined && fileGroupNo !== null && fileGroupNo !== 0) {
            getFileInfoMutation.mutate({ fileGroupNo, page: documentCurrentPage, pageSize: documentItemsPerPage });
          }
        }, [documentCurrentPage, documentItemsPerPage, fileGroupNo, fileUploadTrigger, fileDeleteTrigger]);

        return (
          <div data-testid={tid("documents-tab")}>
            {/* Header with title and Upload file button */}
            <div className="flex items-center justify-between pb-3" data-testid={tid("documents-tab-header")}>
              <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={tid("documents-tab-title")}>
                {t("common.documents")} ({documentTotalData || 0})
              </h3>
              <OutlineButton data-testid={tid("upload-file-button")} onClick={handleUploadFile} disabled={isFileUploading}>
                {isFileUploading ? "Uploading..." : t("common.upload-file")}
              </OutlineButton>
            </div>
            {fileInfoModified && fileInfoModified.length > 0 ? (
              <div data-testid={tid("documents-table")}>
              <DocumentTable
                data={fileInfoModified}
                onDelete={handleDeleteDocument}
                currentPage={documentCurrentPage}
                totalPages={documentTotalPages}
                onPageChange={setDocumentCurrentPage}
                itemsPerPage={documentItemsPerPage}
                totalData={documentTotalData}
              />
              </div>
            ) : (
              <div className={`py-6 text-center ${colors.textSecondary}`} data-testid={tid("documents-empty")}>
                <p data-testid={tid("documents-empty-text")}>
                  {getFileInfoMutation.isPending || isFileUploading
                    ? "Loading files..."
                    : t("project.no-documents-found")}
                </p>
              </div>
            )}
          </div>
        );
      })(),
    },
  ];

  if (getData.isFetching || getLicenseDefault.isPending) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  return (
    <div className={`h-full ${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header with Breadcrumbs */}
      <div className="pb-3" data-testid={tid("header")}>
        <div className="space-y-2">
          <Breadcrumb
            data-testid={tid("breadcrumb")}
            items={[
              { label: t("navbar.previews-list"), href: "/project-previews" },
              { label: t("navbar.previews-details") },
            ]}
          />
          <div>
            <h1 className={`text-2xl font-bold truncate ${colors.textPrimary}`} data-testid={tid("page-title")}>
              {getData.isPending || !displayPreviewData ? "Loading..." : displayPreviewData.projectPreviewName}
            </h1>
          </div>
        </div>
      </div>

      {/* Preview Information Section */}
      <div className={`mb-4 rounded-2xl p-6 ${colors.bgPrimary} ${colors.borderPrimary}`} data-testid={tid("preview-info-section")}>
        <div
          className="mb-2 flex cursor-pointer items-center"
          onClick={() => setIsPreviewInfoCollapsed(!isPreviewInfoCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsPreviewInfoCollapsed(!isPreviewInfoCollapsed);
            }
          }}
          aria-label={isPreviewInfoCollapsed ? "Expand Preview Information" : "Collapse Preview Information"}
          data-testid={tid("preview-info-toggle")}
        >
          <ChevronUpIcon
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${isPreviewInfoCollapsed ? "rotate-180" : ""}`}
            data-testid={tid("preview-info-chevron")}
          />
          <h2 className={`text-md font-semibold ${colors.textPrimary}`} data-testid={tid("preview-info-title")}>
            {isEditMode
              ? t("project.project-preview.edit-preview-information")
              : t("project.project-preview.preview-information")}
          </h2>
        </div>
        {!isPreviewInfoCollapsed && (
          <TabsContainer
            tabs={previewInfoTabs}
            activeTab={activePreviewInfoTab}
            onTabChange={setActivePreviewInfoTab}
            dataTestId={tid("preview-info-tabs")}
          />
        )}
      </div>

      {/* Preview Components Section */}
      <PreviewSection
        title={t("project.project-preview.preview-components-list")}
        projectPreview={previewData}
        lisencePolicySeq={isEditMode || isUpdating ? editFormData.licensePolicySeq : previewData?.licensePolicySeq}
        showActionButtons={true}
        showSelectedComponent={false}
        showCheckbox={true}
        refreshTrigger={previewSectionRefreshTrigger}
        resetSelectionTrigger={resetSelectionTrigger}
        onDeleteComponents={handleDeleteComponents}
        onAddComponents={() => {
          setModalSelectComponentOpen(true);
        }}
        creatorList={creatorList}
        data-testid={tid("preview-components-section")}
      />

      {/* License Selection Modal */}
      <LicenseSelectionModal
        isOpen={modalSelectLicenseOpen}
        onClose={() => setModalSelectLicenseOpen(false)}
        onSelect={handleLicenseSelect}
        selectedLicense={selectedLicense}
        data-testid={tid("license-selection-modal")}
      />

      {/* Select Component Modal */}
      <SelectComponentModal
        isOpen={modalSelectComponentOpen}
        onClose={() => setModalSelectComponentOpen(false)}
        onSelect={handleComponentSelection}
        selectedComponents={previewData?.projectPreviewComponents || []}
        mode="preview-detail"
      />

      {/* Confirm Delete Document Modal */}
      <ConfirmDeleteModal
        data-testid={tid("delete-document-modal")}
        isOpen={showDeleteDocumentModal}
        onClose={() => setShowDeleteDocumentModal(false)}
        onConfirm={handleConfirmDeleteDocument}
        title={t("project.delete-document-confirmation")}
        description={t("project.delete-document-description", {
          fileName: documentToDelete?.realFileName || "this document",
        })}
        loading={deleteDocumentLoading}
      />
    </div>
  );
};

export default PreviewDetail;
